import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, IdCard } from "lucide-react";
import { readImageFileAsDataUrl } from "@/utils/imageFile";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import {
  buildSignupQuery,
  getSignupHKID,
  readSignupParams,
  setSignupHKID,
} from "./signupState";

/**
 * Signup step 5 — HKID. Ports the RN `HKIDUploadScreen`.
 * Unlike the student card step this one is fully optional: the image is
 * downscaled, kept as a data URL in signup state and uploaded during
 * registration. The store is the source of truth, so the preview rehydrates on
 * re-entry (empty when the step was skipped).
 */
const HKIDPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readSignupParams(search);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [hkid, setHKID] = useState<string>(getSignupHKID);
  const [imageError, setImageError] = useState("");

  const pickImage = async (file?: File) => {
    if (!file) return;
    const dataUrl = await readImageFileAsDataUrl(file);
    if (!dataUrl) return;
    setHKID(dataUrl);
    setImageError(
      setSignupHKID(dataUrl) ? "" : t("Could not cache this photo. It will still be uploaded when you register."),
    );
  };

  const onRemoveImage = () => {
    setHKID("");
    setSignupHKID("");
    setImageError("");
  };

  const goNext = () => {
    navigate(
      `/signup/basic${buildSignupQuery({
        ...params,
        hkid: hkid ? "set" : "",
      })}`,
    );
  };

  const onPressSkip = () => {
    setHKID("");
    setSignupHKID("");
    goNext();
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">
        {t("Upload your HKID (Optional)")}
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        {t("Uninfo will verify your HKID in 24 hrs")}
      </p>

      <div className="mt-8 flex flex-col items-center">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-[#ffa217] bg-[#fff8f0] transition hover:bg-[#fff3e0]"
        >
          {hkid ? (
            <img src={hkid} alt="HKID" className="h-full w-full object-cover" />
          ) : (
            <IdCard size={80} className="text-[#ffa217]" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pickImage(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
        >
          <Camera size={16} />
          {hkid ? t("Change photo") : t("Pick an image")}
        </button>
        {hkid && (
          <button
            type="button"
            onClick={onRemoveImage}
            className="mt-2 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
          >
            {t("REMOVE")}
          </button>
        )}
        {imageError && (
          <p className="mt-2 max-w-xs text-center text-sm font-medium text-red-500">
            {imageError}
          </p>
        )}
      </div>

      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={goNext}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
        >
          {t("NEXT")}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/signup/student-card${buildSignupQuery({ ...params })}`)}
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
        <button
          type="button"
          onClick={onPressSkip}
          className="w-full rounded-lg bg-gray-400 py-3 font-bold text-white transition hover:bg-gray-500"
        >
          {t("SKIP")}
        </button>
      </div>
    </div>
  );
};

export default HKIDPage;
